import React from 'react';
import { Route, Switch, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { AppProvider } from "./lib/context";
import { UpdateOverlay } from "./components/update-overlay";
import Navbar from "./components/navbar";
import { InAppBackButton } from "./components/in-app-back-button";
import { useEffect, useState, useContext, lazy, Suspense } from "react";
import { User } from "@shared/schema";
import AppLayout from "./components/app-layout";
import { AuthProvider, useAuth, AuthContext } from "./hooks/use-auth";
import { TelegramBackButton } from "./components/telegram-back-button";
import { getToken } from "./lib/token-storage";
import UsernameRequiredScreen from './components/username-required-screen';
import BannedScreen from './components/banned-screen';
import { apiService } from "./lib/api-helper";
import { LoadingScreen } from "./components/ui/loading-screen";
import { socketService } from "./lib/socket-service";
import { isTelegramWebApp, expandApp, notifyAppReady } from "./lib/telegram-service";
import { apiRequest } from "./lib/queryClient";
import { useMessageBanner } from "./components/ui/message-banner";
import { TonProvider } from "./lib/ton-connect";
import { FloatingRegisterButton } from "./components/guest";
import PartnerConfirmationModal from "./components/partner-confirmation-modal";
import { useMaintenanceMode } from "./hooks/use-maintenance-mode";
import { usePresence } from "./hooks/use-presence";
import { DetailPanelProvider } from "./components/layout/detail-panel-context";

// Lazy-loaded page components (code splitting)
const DiscoverPage = lazy(() => import('./pages/discover'));
const Speed = lazy(() => import('@/pages/speed'));
const Events = lazy(() => import('./pages/events'));
const WallPage = lazy(() => import('./pages/wall'));
const GroupsPage = lazy(() => import('./pages/groups'));
const ChatPage = lazy(() => import('./pages/chat'));
const RegisterPage = lazy(() => import('./pages/register-page'));
const AdminPage = lazy(() => import('./pages/admin'));
const AdminGroupsPage = lazy(() => import('./pages/admin-groups'));
const AdminTools = lazy(() => import('./pages/admin/admin-tools'));
const OrganizerPage = lazy(() => import('./pages/organizer'));
const CreatorPanel = lazy(() => import('./pages/creator-panel'));
const LinkPartnerPage = lazy(() => import('./pages/link-partner'));
const NotFound = lazy(() => import('./pages/not-found'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsAndConditions = lazy(() => import('./pages/terms-and-conditions'));
const PublicEventPage = lazy(() => import('./pages/public-event'));
const MaintenancePage = lazy(() => import('./pages/maintenance-page'));
const PhotoManager2Onboarding = lazy(() => import('./components/photo-manager-2-onboarding'));
const TelegramOnlyLanding = lazy(() => import('./components/telegram-only-landing'));
const DesktopNotSupported = lazy(() => import('./components/desktop-not-supported'));
const WebLoginPage = lazy(() => import('./components/auth/telegram-login-widget').then(m => ({ default: m.WebLoginPage as any })));
const MarketingLanding = lazy(() => import('./pages/marketing-landing'));
const ExternalUserFlow = lazy(() => import('./components/onboarding/external-user-flow'));
const RejectedScreen = lazy(() => import('./components/rejected-screen'));
const UnifiedWallModal = lazy(() => import('./components/unified-wall-modal'));
const PrebanWarningModal = lazy(() => import('./components/preban-warning-modal'));
const GlobalProfileHandler = lazy(() => import('./components/global-profile-handler').then(m => ({ default: m.GlobalProfileHandler })));
const GlobalTravelHandler = lazy(() => import('./components/global-travel-handler').then(m => ({ default: m.GlobalTravelHandler })));

function AuthenticatedRouter() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const { showMessageBanner, BannerContainer } = useMessageBanner();
  const [showPrebanModal, setShowPrebanModal] = useState(false);

  // Attiva il sistema di presenza per inviare heartbeat
  usePresence();

  // Mostra modale pre-ban se l'utente è pre-bannato
  useEffect(() => {
    if (user?.isPreBanned) {
      setShowPrebanModal(true);
    }
  }, [user?.isPreBanned]);

  // 🔥 WebSocket listener per banner notifiche messaggi
  useEffect(() => {
    if (!user?.id) return;

    let cleanup: (() => void) | null = null;

    import('@/lib/socket-service').then(({ socketService }) => {
      console.log('[APP] 🔌 Setting up WebSocket listener for message banners');

      const handleNewMessage = (data: any) => {
        console.log('[APP] 📨 New message received for banner:', data);

        // Non mostrare banner se siamo nella pagina chat
        if (location === '/chat') {
          console.log('[APP] 📨 User is in chat page, skipping banner');
          return;
        }

        // Non mostrare banner se il messaggio è inviato dall'utente corrente
        if (data.senderId === user.id) {
          console.log('[APP] 📨 Message from current user, skipping banner');
          return;
        }

        // Mostra il banner con il mittente e l'anteprima del messaggio
        const senderName = data.senderName || data.senderUsername || 'Nuovo messaggio';
        const messagePreview = data.content?.length > 50 
          ? data.content.substring(0, 50) + '...' 
          : data.content || 'Nuovo messaggio';
        
        // Callback per aprire la chat quando si clicca sul banner
        const openChat = () => {
          // Dispatch event to open chat with specific user
          const event = new CustomEvent('openChat', {
            detail: { 
              userId: data.senderId,
              interactionId: data.interactionId 
            }
          });
          window.dispatchEvent(event);
          setLocation('/chat');
        };
        
        showMessageBanner(
          senderName, 
          messagePreview, 
          data.senderPhotoUrl,
          data.senderAge,
          data.senderPartnerAge,
          data.senderCategory,
          data.isVip,
          openChat
        );
      };

      socketService.on('new-chat-message', handleNewMessage);

      cleanup = () => {
        socketService.off('new-chat-message', handleNewMessage);
      };
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [user?.id, location, showMessageBanner, setLocation]);

  return (
    <AppLayout>
      {/* Banner Container per notifiche messaggi */}
      <BannerContainer />
      <Suspense fallback={<div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" /></div>}>
        <Switch>
          <Route path="/" component={DiscoverPage} />
          <Route path="/speed" component={Speed} />
          <Route path="/wall" component={WallPage} />
          <Route path="/events" component={Events} />
          <Route path="/groups" component={GroupsPage} />
          <Route path="/chat" component={ChatPage} />
          <Route path="/discover" component={DiscoverPage} />
          <Route path="/link-partner/:token" component={LinkPartnerPage} />
          <Route path="/admin" component={AdminPage} />
          <Route path="/admin-groups" component={AdminGroupsPage} />
          <Route path="/admin-tools" component={AdminTools} />
          <Route path="/organizer" component={OrganizerPage} />
          <Route path="/creator-panel" component={CreatorPanel} />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/terms-and-conditions" component={TermsAndConditions} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
      <Navbar />
      {/* Tasto indietro in-app per la PWA standalone (in Telegram si usa il BackButton nativo) */}
      <InAppBackButton />
      {/* Componente per gestire il pulsante indietro di Telegram */}
      <TelegramBackButton />
      {/* Pulsante floating per registrazione guest */}
      <FloatingRegisterButton />
      {/* Global handler for opening user profiles from anywhere */}
      <Suspense fallback={null}>
        <GlobalProfileHandler />
      </Suspense>
      {/* Global handler for opening travel creation from anywhere */}
      <Suspense fallback={null}>
        <GlobalTravelHandler />
      </Suspense>

      {/* Pre-ban warning modal */}
      {showPrebanModal && user?.isPreBanned && (
        <Suspense fallback={null}>
          <PrebanWarningModal
            reason={user.preBanReason || undefined}
            preBannedAt={(user.preBannedAt || undefined) as any}
            onClose={() => setShowPrebanModal(false)}
          />
        </Suspense>
      )}
    </AppLayout>
  );
}

function PublicRouter() {
  // Verifica se siamo in un percorso di registrazione specifico
  const isRegistrationPath = window.location.pathname === '/auth/register';
  console.log('[PublicRouter] Percorso attuale:', window.location.pathname, 'isRegistrationPath:', isRegistrationPath);

  return (
    <Suspense fallback={<div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" /></div>}>
      <Switch>
        <Route path="/auth/register" component={RegisterPage} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-and-conditions" component={TermsAndConditions} />
        <Route path="/event/:token" component={PublicEventPage} />
        <Route path="/" component={isRegistrationPath ? RegisterPage : RegisterPage} />
        <Route component={RegisterPage} />
      </Switch>
    </Suspense>
  );
}

interface CheckUserPhotosProps {
  user: any;
  token: string | null;
  children: React.ReactNode;
  onShouldShowRegistration: (shouldShow: boolean) => void;
}

// Componente per controllare se l'utente ha foto
function CheckUserPhotos({ user, token, children, onShouldShowRegistration }: CheckUserPhotosProps) {
  console.log('[CheckUserPhotos] === COMPONENTE INIZIALIZZATO ===');
  console.log('[CheckUserPhotos] User:', user?.username);
  console.log('[CheckUserPhotos] Token presente:', !!token);

  // TUTTI GLI HOOKS DEVONO ESSERE CHIAMATI PRIMA DI QUALSIASI RETURN CONDIZIONALE

  // Query per le foto - SEMPRE chiamato, mai condizionale
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: refetchPhotos
  } = useQuery({
    queryKey: ['/api/users/photos'],
    queryFn: async () => {
      console.log('[CheckUserPhotos] 🔍 Inizio controllo foto utente');
      console.log('[CheckUserPhotos] User:', user?.username, 'ID:', user?.id);
      console.log('[CheckUserPhotos] Token present:', !!token);

      if (!user || !token) {
        console.log('[CheckUserPhotos] ⚠️ Missing user or token, skipping photo check');
        return { success: false, photos: [] };
      }

      try {
        const response = await apiRequest('GET', '/api/users/photos');
        console.log('[CheckUserPhotos] ✅ Foto recuperate con successo:', response.photos?.length || 0);
        return response;
      } catch (error: any) {
        console.error('[CheckUserPhotos] ❌ Errore recupero foto:', error);
        // In caso di errore di rete o altro, considera l'utente come senza foto
        // piuttosto che bloccare l'app
        console.log('[CheckUserPhotos] Errore nel recupero foto, considerando utente senza foto per continuare');
        return { success: false, photos: [] };
      }
    },
    enabled: !!user && !!token,
    staleTime: 1000 * 60 * 15, // AUMENTATO a 15 minuti per prevenire race conditions
    refetchOnMount: false, // NON refetch al mount per evitare race conditions con upload gruppo
    refetchOnWindowFocus: false, // NON refetch al focus per stabilità
    retry: 1, // Ridotto il numero di retry
    retryDelay: 1000 // Aggiungi un delay tra i retry
  });

  // ✅ FIX: Rimosso completamente il loader con delay
  // Non serve mostrare un loader durante il check delle foto

  // DOPO TUTTI GLI HOOKS, ORA POSSIAMO FARE I RETURN CONDIZIONALI

  // Log dei dati per debug
  console.log('[CheckUserPhotos] === STATO QUERY ===');
  console.log('[CheckUserPhotos] isLoading:', isLoading);
  console.log('[CheckUserPhotos] data:', data);
  console.log('[CheckUserPhotos] error:', error);

  // ✅ FIX: Rimosso il loader durante isLoading
  // Mostriamo direttamente i children per evitare loop infiniti

  // Log per debug delle foto
  console.log('[CheckUserPhotos] === CONTROLLO VALIDITÀ FOTO ===');
  console.log('[CheckUserPhotos] Raw data received:', data);
  console.log('[CheckUserPhotos] Data type:', typeof data);
  console.log('[CheckUserPhotos] Data keys:', data ? Object.keys(data) : 'no data');

  // Debug più dettagliato della struttura dati
  if (data) {
    console.log('[CheckUserPhotos] Success flag:', data.success);
    console.log('[CheckUserPhotos] Photos property:', data.photos);
    console.log('[CheckUserPhotos] Photos type:', typeof data.photos);
    console.log('[CheckUserPhotos] Is photos array?:', Array.isArray(data.photos));
    console.log('[CheckUserPhotos] Photos length:', data.photos?.length);

    if (data.photos && Array.isArray(data.photos)) {
      console.log('[CheckUserPhotos] Individual photos:');
      data.photos.forEach((photo, index) => {
        console.log(`[CheckUserPhotos] Photo ${index}:`, photo);
      });
    }
  }

  console.log('[CheckUserPhotos] === RISULTATO CONTROLLO ===');
  console.log('[CheckUserPhotos] Development mode:', process.env.NODE_ENV === 'development');

  // PROTEZIONE RACE CONDITION: Se la query sta fetching (refetch in background), usa i dati cached
  // Questo previene il flash di PhotoManager2Onboarding durante upload gruppo
  if (isFetching && data) {
    console.log('[CheckUserPhotos] 🔄 Background refetch in corso, usando dati cached per evitare race condition');
    // Continua con i dati cached invece di mostrare loading
  }

  // Controllo aggiuntivo: se il caricamento è in corso E non ci sono dati cached, non mostrare l'onboarding
  if (isLoading && !data) {
    console.log('[CheckUserPhotos] ⏳ Caricamento iniziale in corso, mostrando children');
    onShouldShowRegistration(false);
    return <>{children}</>;
  }

  // Se c'è un errore di rete o simile, permetti comunque l'accesso all'app
  if (error && !data) {
    console.log('[CheckUserPhotos] ⚠️ Errore nel recupero foto senza dati cached, permettendo accesso app:', error);
    onShouldShowRegistration(false);
    return <>{children}</>;
  }

  // LOGICA MIGLIORATA: Controlla se l'utente ha foto PERSONALI valide
  // NOTA: Questo controllo è SOLO per le foto del profilo utente,
  // NON per le foto dei gruppi (che sono gestite separatamente)

  // ⭐ PRIORITÀ 1 (PIÙ AFFIDABILE): Verifica se l'utente ha profile_photo_id dal database
  // Se presente, l'utente HA una foto - INDIPENDENTEMENTE dallo stato della query API
  const hasProfilePhotoId = user?.profile_photo_id != null && user.profile_photo_id > 0;

  // PRIORITÀ 2 (FALLBACK): Verifica tramite query API solo se profile_photo_id non è presente
  const hasValidResponse = data && data.success !== false;
  const hasPhotosInArray = Array.isArray(data?.photos) && data.photos.length > 0;
  const hasValidPhotosFlag = data?.hasValidPhotos === true;

  // ✅ FIX CRITICO: L'utente ha foto se:
  // 1. PRIMA: Ha profile_photo_id nel database (fonte più affidabile)
  // 2. OPPURE: La query API conferma presenza foto
  // Questo previene false-negative quando la query API fallisce temporaneamente
  const hasPhotos = hasProfilePhotoId || (hasValidResponse && (hasPhotosInArray || hasValidPhotosFlag));

  console.log('[CheckUserPhotos] ===== CONTROLLO FOTO UTENTE (NON GRUPPO) =====');
  console.log('[CheckUserPhotos] Has valid response:', hasValidResponse);
  console.log('[CheckUserPhotos] Has photos in array:', hasPhotosInArray);
  console.log('[CheckUserPhotos] Photos count:', data?.photos?.length || 0);
  console.log('[CheckUserPhotos] Has valid photos flag:', hasValidPhotosFlag);
  console.log('[CheckUserPhotos] Has profile photo ID:', hasProfilePhotoId, '(ID:', user?.profile_photo_id || 'none', ')');
  console.log('[CheckUserPhotos] Final has photos:', hasPhotos);
  console.log('[CheckUserPhotos] User ID:', user?.id);
  console.log('[CheckUserPhotos] Username:', user?.username);

  // Se l'utente NON ha foto PERSONALI valide, mostra PhotoManager2Onboarding
  if (!hasPhotos) {
    console.log('[CheckUserPhotos] ❌ Utente SENZA foto PERSONALI valide, mostrando PhotoManager2Onboarding');
    console.log('[CheckUserPhotos] NOTA: Le foto dei gruppi sono separate e non influenzano questo controllo');
    onShouldShowRegistration(true);
    return <Suspense fallback={<LoadingScreen />}><PhotoManager2Onboarding /></Suspense>;
  }

  // Se l'utente ha foto PERSONALI valide, mostra l'app
  console.log('[CheckUserPhotos] ✅ Utente con foto PERSONALI valide, mostrando AuthenticatedRouter');
  onShouldShowRegistration(false);
  return <>{children}</>;
}

// Componente di routing con controllo autenticazione ottimizzato
function RouterWithAuth() {
  const { authState, token } = useAuth();
  const [isPartnerConfirmationModalOpen, setIsPartnerConfirmationModalOpen] = useState(false);

  useEffect(() => {
    console.log('[App.tsx] Check modale partner - authState.user:', authState.user);
    console.log('[App.tsx] user.partnerLinkedBy:', authState.user?.partnerLinkedBy);
    console.log('[App.tsx] user.partnerTelegramId:', authState.user?.partnerTelegramId);
    console.log('[App.tsx] pendingPartnerInvitation:', authState.pendingPartnerInvitation);

    // CASO 1: Utente autenticato con partnerLinkedBy
    if (authState.isAuthenticated && authState.user) {
      const user = authState.user;
      if (user.partnerLinkedBy && !user.partnerTelegramId) {
        console.log('[App.tsx] 💑 APRENDO MODALE PARTNER per:', user.partnerLinkedBy);
        setIsPartnerConfirmationModalOpen(true);
      }
    }

    // CASO 2: Utente NON autenticato con pending partner invitation
    if (authState.pendingPartnerInvitation) {
      console.log('[App.tsx] 💑 APRENDO MODALE PARTNER per pending invitation');
      setIsPartnerConfirmationModalOpen(true);
    }
  }, [authState.isAuthenticated, authState.user, authState.pendingPartnerInvitation]);

  // ⭐⭐⭐ TUTTI GLI HOOKS DEVONO ESSERE CHIAMATI PRIMA DI QUALSIASI RETURN CONDIZIONALE
  // 🔥 CRITICAL FIX: Inizializza WebSocket quando l'utente è autenticato
  useEffect(() => {
    if (authState?.isAuthenticated && authState?.user?.id) {
      console.log('[RouterWithAuth] 🚀 Inizializzazione WebSocket per user:', authState.user.id);
      socketService.connect(authState.user.id);

      return () => {
        console.log('[RouterWithAuth] 🧹 Cleanup WebSocket connection');
        socketService.disconnect();
      };
    }
  }, [authState?.isAuthenticated, authState?.user?.id]);

  // ⭐ PRIORITÀ ASSOLUTA #1: Flag just_registered - CONTROLLO DOPO TUTTI GLI HOOKS
  const justRegistered = sessionStorage.getItem('just_registered') === 'true';
  if (justRegistered) {
    console.log('[RouterWithAuth] 🎉🎉🎉 FLAG just_registered TROVATO - MOSTRANDO PhotoManager2Onboarding IMMEDIATAMENTE');
    console.log('[RouterWithAuth] ⚡ SKIP: authState check, mobile check, tutto bypassato');

    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <Suspense fallback={<LoadingScreen />}>
          <PhotoManager2Onboarding />
        </Suspense>
        <Toaster />
      </div>
    );
  }

  // ⭐⭐⭐ PRIORITÀ #0: MODALITÀ MANUTENZIONE - Controlla se l'app è in manutenzione
  // Questo deve essere DOPO just_registered perché l'onboarding deve funzionare sempre
  const { shouldShowMaintenance, isLoading: isMaintenanceLoading } = useMaintenanceMode();
  
  if (isMaintenanceLoading) {
    console.log('[RouterWithAuth] → Controllo modalità manutenzione in corso...');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
        <LoadingScreen />
        <Toaster />
      </div>
    );
  }

  if (shouldShowMaintenance) {
    console.log('[RouterWithAuth] → Modalità manutenzione ATTIVA - mostro pagina work in progress');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <Suspense fallback={<LoadingScreen />}>
          <MaintenancePage userLanguage={authState?.user?.language} />
        </Suspense>
        <Toaster />
      </div>
    );
  }

  console.log('[RouterWithAuth] ========================================');
  console.log('[RouterWithAuth] Stato corrente:', authState);
  console.log('[RouterWithAuth] isGuest:', authState?.isGuest);
  console.log('[RouterWithAuth] isAuthenticated:', authState?.isAuthenticated);
  console.log('[RouterWithAuth] needsRegistration:', authState?.needsRegistration);
  console.log('[RouterWithAuth] initializing:', authState?.initializing);
  console.log('[RouterWithAuth] hydrationComplete:', authState?.hydrationComplete);
  console.log('[RouterWithAuth] telegramCheckComplete:', authState?.telegramCheckComplete);
  console.log('[RouterWithAuth] ========================================');

  // PROTEZIONE ANTI-LOOP: Mostra loading solo se authState è completamente non definito
  // o se è esplicitamente in fase di loading iniziale
  if (!authState) {
    console.log('[RouterWithAuth] → AuthState undefined, showing loader');
    return <LoadingScreen />;
  }

  // PRIORITÀ 1: Mostra loader durante l'inizializzazione O se hydration non è completa
  if (authState.initializing || authState.loading || !authState.hydrationComplete) {
    console.log('[RouterWithAuth] → Inizializzazione in corso... (initializing:', authState.initializing, 'loading:', authState.loading, 'hydrationComplete:', authState.hydrationComplete, ')');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
        <LoadingScreen />
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ WEB: Se siamo fuori Telegram e non autenticati → mostra login web
  if (!isTelegramWebApp() && !authState.isAuthenticated && !authState.initializing) {
    const savedWebToken = getToken();
    if (!savedWebToken) {
      console.log('[RouterWithAuth] → Web browser, non autenticato → MarketingLanding');
      return (
        <div className="w-full relative">
          <Suspense fallback={<LoadingScreen />}>
            {React.createElement(MarketingLanding as any, {
              onLoginSuccess: (_user: any, _token: string) => {
                console.log('[RouterWithAuth] Web login success, reloading...');
                window.location.reload();
              }
            })}
          </Suspense>
          <Toaster />
        </div>
      );
    }
  }

  // PRIORITÀ 1: Utente senza username Telegram (condizione più specifica)
  if (authState.needsUsername && authState.telegramUser) {
    console.log('[RouterWithAuth] → Username richiesto');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <UsernameRequiredScreen
          telegramUser={authState.telegramUser}
          onRetry={() => {
            console.log('[RouterWithAuth] Retry richiesto per username');
            window.location.reload();
          }}
        />
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 2.5: Pending partner invitation (mostra modale conferma)
  if (authState.pendingPartnerInvitation) {
    console.log('[RouterWithAuth] → Pending partner invitation, mostrando modale');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center text-white">
            <h2 className="text-xl font-bold mb-2">Invito Partner</h2>
            <p>Stai per essere aggiunto come partner...</p>
          </div>
        </div>
        <PartnerConfirmationModal
          isOpen={isPartnerConfirmationModalOpen}
          onClose={() => setIsPartnerConfirmationModalOpen(false)}
          userLanguage={authState?.user?.language}
        />
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 2.5: Utente bannato o rifiutato
  if (authState.isBanned) {
    // Rifiuto temporaneo (primo rifiuto) - mostra schermata con countdown 24 ore
    if (authState.banType === 'rejected') {
      console.log('[RouterWithAuth] → Utente RIFIUTATO (temporaneo), mostrando RejectedScreen con countdown');
      return (
        <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
          <Suspense fallback={<LoadingScreen />}>
            <RejectedScreen
              reason={authState.banReason}
              canReapplyAt={authState.canReapplyAt}
              rejectionCount={authState.rejectionCount}
            />
          </Suspense>
          <Toaster />
        </div>
      );
    }

    // Ban permanente o rifiuto permanente - mostra schermata di ban
    console.log('[RouterWithAuth] → Utente BANNATO/rifiutato permanentemente, mostrando BannedScreen');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <BannedScreen banReason={authState.banReason} />
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 3: Utente guest - DISABILITATO: ora mostra direttamente il form di registrazione
  // invece di mostrare l'app con la griglia blurrata
  if (authState.isGuest && !authState.isAuthenticated) {
    console.log('[RouterWithAuth] → Utente guest, mostrando form di registrazione');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <Suspense fallback={<LoadingScreen />}>
          <RegisterPage />
        </Suspense>
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 4: Utente autenticato correttamente (controllo normale foto)
  if (authState.isAuthenticated && authState.user) {
    console.log('[RouterWithAuth] → Utente autenticato, controllo foto');

    // Note: Banned users are now in banned_rejected_users table, not in users
    // They will be caught at login (auth.ts) and won't reach here
    // isPreBanned is handled by PrebanWarningModal component

    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <CheckUserPhotos
          user={authState.user}
          token={token}
          onShouldShowRegistration={(shouldShow) => {
            if (shouldShow) {
              console.log('[RouterWithAuth] CheckUserPhotos: utente senza foto, mostrando PhotoManager2Onboarding');
              // Il componente mostrerà PhotoManager2Onboarding nel return
            } else {
              console.log('[RouterWithAuth] CheckUserPhotos: utente ha foto, mostrando AuthenticatedRouter');
            }
          }}
        >
          <AuthenticatedRouter />
        </CheckUserPhotos>
        <Toaster />
        <PartnerConfirmationModal
            isOpen={isPartnerConfirmationModalOpen}
            onClose={() => setIsPartnerConfirmationModalOpen(false)}
            userLanguage={authState?.user?.language}
        />
      </div>
    );
  }

  // PRIORITÀ 5: Utente necessita application (non in gruppo approvato)
  if (authState.needsApplication && authState.telegramUser) {
    console.log('[RouterWithAuth] → Application richiesta (utente non in gruppo approvato)');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <Suspense fallback={<LoadingScreen />}>
          <ExternalUserFlow
            telegramId={String(authState.telegramUser.id)}
            telegramUsername={authState.telegramUser.username}
            telegramFirstName={authState.telegramUser.first_name}
            language="it"
            onApproved={() => {
              console.log('[RouterWithAuth] Application approved, reloading...');
              window.location.reload();
            }}
          />
        </Suspense>
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 6: Utente necessita registrazione
  // ⭐ ANTI-FLASH DEFINITIVO: Mostra il form SOLO SE:
  // 1. needsRegistration è true
  // 2. telegramCheckComplete è true  
  // 3. NON c'è un token salvato
  // 4. hydrationComplete è true (risposta definitiva ricevuta)
  // 5. ⚠️ CRITICO: NON stiamo ancora inizializzando (initializing: false)
  const savedToken = getToken();
  if (authState.needsRegistration && authState.telegramCheckComplete && authState.hydrationComplete && !authState.initializing) {
    // Se c'è un token salvato, l'utente potrebbe essere già registrato - mostra loader invece del form
    if (savedToken) {
      console.log('[RouterWithAuth] → needsRegistration=true MA token presente, mostrando loader invece del form...');
      return (
        <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
          <LoadingScreen />
          <Toaster />
        </div>
      );
    }
    console.log('[RouterWithAuth] → Registrazione richiesta (telegramCheckComplete:', authState.telegramCheckComplete, ', hydrationComplete:', authState.hydrationComplete, ', initializing:', authState.initializing, ')');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
        <Suspense fallback={<LoadingScreen />}>
          <RegisterPage />
        </Suspense>
        <Toaster />
      </div>
    );
  }
  
  // ⚠️ ANTI-FLASH: Se needsRegistration=true MA initializing=true, aspetta!
  if (authState.needsRegistration && authState.initializing) {
    console.log('[RouterWithAuth] → needsRegistration=true MA ancora initializing, mostrando loader...');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
        <LoadingScreen />
        <Toaster />
      </div>
    );
  }

  // PRIORITÀ 7: Telegram check non ancora completo - mostra loading
  if (!authState.telegramCheckComplete) {
    console.log('[RouterWithAuth] → Telegram check in corso, mostrando loader...');
    return (
      <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
        <LoadingScreen />
        <Toaster />
      </div>
    );
  }

  // FALLBACK: Stato intermedio - mostra loader (NON RegisterPage)
  // Questo può accadere durante transizioni di stato, es. quando auth-state-manager
  // emette un evento vuoto prima che checkRegistrationStatus() completi
  console.log('[RouterWithAuth] → Stato intermedio, mostrando loader in attesa di stato definitivo...');
  console.log('[RouterWithAuth] Debug state:', { 
    isAuthenticated: authState.isAuthenticated,
    needsRegistration: authState.needsRegistration,
    needsUsername: authState.needsUsername,
    needsApplication: authState.needsApplication,
    telegramCheckComplete: authState.telegramCheckComplete,
    initializing: authState.initializing,
    loading: authState.loading
  });

  return (
    <div className="tg-app w-full tg-bg tg-text shadow-sm flex items-center justify-center min-h-screen">
      <LoadingScreen />
      <Toaster />
    </div>
  );
}

function App() {
  // ⚠️ ANTI-FLASH: Inizializza a null per mostrare loader durante il rilevamento
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [isInTelegram, setIsInTelegram] = useState<boolean | null>(null);
  const { showMessageBanner, BannerContainer } = useMessageBanner();

  useEffect(() => {
    // Verifica se siamo su dispositivo mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Rileva se siamo in ambiente Telegram
    // IMPORTANTE: isTelegramWebApp() verifica che initData.length > 0
    // Lo script telegram-web-app.js crea sempre window.Telegram.WebApp,
    // ma initData è popolato SOLO quando l'app è aperta da Telegram

    const checkTelegram = () => {
      const isInTg = isTelegramWebApp();
      const initDataLength = window.Telegram?.WebApp?.initData?.length || 0;

      console.log('[App] Telegram check:', {
        isTelegramWebApp: isInTg,
        initDataLength,
        hasTelegramObject: !!window.Telegram?.WebApp
      });

      if (isInTg) {
        console.log('[App] ✅ Running INSIDE Telegram WebApp');
        // Non chiamare expand/fullscreen su rotte pubbliche (es. /event/:token aperto dal browser in-app)
        // perché il browser in-app di Telegram ha "Telegram" nello user agent ma NON è una Mini App
        const isOnPublicPath = window.location.pathname.startsWith('/event/') ||
                               window.location.pathname.startsWith('/privacy-policy') ||
                               window.location.pathname.startsWith('/terms-and-conditions');
        if (!isOnPublicPath) {
          notifyAppReady();
          expandApp();
        }
        setIsInTelegram(true);
      } else {
        console.log('[App] ❌ Running OUTSIDE Telegram (initData is empty)');
        setIsInTelegram(false);
      }
    };

    // Piccolo delay per assicurarsi che lo script Telegram sia caricato
    const timeoutId = setTimeout(checkTelegram, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Integra listener WebSocket per banner messaggi nell'App
  // ⚠️ IMPORTANTE: Questo useEffect DEVE essere chiamato PRIMA di qualsiasi return condizionale
  useEffect(() => {
    // Importa socketService in modo lazy per evitare dipendenze circolari
    import('@/lib/socket-service').then(({ socketService }) => {
      const handleNewMessage = (data: any) => {
        console.log('[APP] 📨 Nuovo messaggio ricevuto:', data);

        const senderName = data.message?.sender?.username || 'Qualcuno';
        const messagePreview = data.message?.content?.substring(0, 100) || '';
        const senderPhotoUrl = data.message?.sender?.photos?.[0]?.url;

        // Mostra banner invece di toast
        showMessageBanner(senderName, messagePreview, senderPhotoUrl);
      };

      socketService.on('new-chat-message', handleNewMessage);

      return () => {
        socketService.off('new-chat-message', handleNewMessage);
      };
    });
  }, [showMessageBanner]);

  // Rileva se siamo in sviluppo (controllo robusto basato su hostname)
  const isDevEnvironment =
    import.meta.env.MODE === 'development' ||
    window.location.hostname.includes('replit.dev') ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  // ✅ CRITICAL FIX: QueryClientProvider DEVE wrappare TUTTO
  // Questo assicura che tutti i componenti abbiano accesso al QueryClient
  // Calcola quale schermata mostrare usando la logica precedente
  let content: React.ReactNode;

  // L'app vera e propria
  const appContent = (
    <TonProvider>
      <AuthProvider>
        <AppContextWrapper>
          <RouterWithAuth />
          <Toaster />
          <BannerContainer />
        </AppContextWrapper>
      </AuthProvider>
    </TonProvider>
  );

  // Contenuto per pagine pubbliche (senza autenticazione)
  const publicContent = (
    <div className="tg-app w-full tg-bg tg-text shadow-sm relative">
      <PublicRouter />
      <Toaster />
    </div>
  );

  // Route pubbliche accessibili anche da browser esterni (senza Telegram)
  const publicPaths = ['/privacy-policy', '/terms-and-conditions'];
  // Check if it's a valid event public link (must have a token after /event/)
  const isEventPublicPath = window.location.pathname.startsWith('/event/') &&
                            window.location.pathname.length > '/event/'.length;
  const isPublicPath = publicPaths.some(path => window.location.pathname.startsWith(path)) || isEventPublicPath;

  // ⚠️ ANTI-FLASH: Mostra loader mentre verifichiamo ambiente Telegram O tipo dispositivo
  if (isInTelegram === null || isMobile === null) {
    // Mostra loader mentre verifichiamo l'ambiente
    content = <LoadingScreen />;
  } else if (isPublicPath) {
    // Route pubblica (eventi, privacy, terms) → SEMPRE accessibile senza auth,
    // anche dal browser in-app di Telegram che ha "Telegram" nello user agent
    content = publicContent;
  } else if (isInTelegram === false && !getToken()) {
    // FAST PATH: visitatore web NON loggato (niente token) → mostra subito la
    // landing pubblica, senza attendere l'inizializzazione auth (non c'è nulla
    // da idratare senza token). Evita il loader lungo prima della home.
    content = (
      <div className="w-full relative">
        <Suspense fallback={<LoadingScreen />}>
          {React.createElement(MarketingLanding as any, {
            onLoginSuccess: () => window.location.reload(),
          })}
        </Suspense>
        <Toaster />
      </div>
    );
  } else if (isInTelegram === false) {
    // FUORI TELEGRAM (web browser) con token → app (l'auth web avviene tramite
    // Telegram Login Widget e la sessione viene idratata da RouterWithAuth)
    content = appContent;
  } else if (isMobile === false) {
    // IN TELEGRAM ma NON mobile.
    // Il nuovo desktop shell (sidebar + top bar) è attivo di default.
    // Escape locale: localStorage.kg_desktop_shell = '0'
    // Override build-time: VITE_ENABLE_DESKTOP=0 per forzare il fallback.
    const localOff =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('kg_desktop_shell') === '0';
    const buildOff = import.meta.env.VITE_ENABLE_DESKTOP === '0';
    content = (localOff || buildOff)
      ? <Suspense fallback={<LoadingScreen />}><DesktopNotSupported /></Suspense>
      : appContent;
  } else {
    // IN TELEGRAM e MOBILE → app normale a tutto schermo
    content = appContent;
  }

  // ✅ CRITICAL FIX v3: All paths wrapped with QueryClientProvider - BUILD FORCED
  return (
    <QueryClientProvider client={queryClient}>
      <DetailPanelProvider>
        {content}
      </DetailPanelProvider>
      <UpdateOverlay />
    </QueryClientProvider>
  );
}

export default App;

// Componente per gestire il Wall Modal - DEVE essere figlio di AppProvider
function WallModalManager() {
  const [isWallModalOpen, setIsWallModalOpen] = useState(false);
  const [createPostMode, setCreatePostMode] = useState<'tweet' | 'post'>('tweet');
  const [createPostFile, setCreatePostFile] = useState<{ file: File; type: 'image' | 'video' } | null>(null);
  const authContext = useContext(AuthContext);
  
  const token = authContext?.token;

  // Listener per l'evento openCreatePost dal header
  useEffect(() => {
    const handleOpenCreatePost = (e: Event) => {
      console.log('[WallModalManager] 📝 Apertura modal wall post');
      // Chiudi il centro notifiche dell'header prima di aprire il modale wall
      const closeNotificationEvent = new CustomEvent('closeNotificationsPanel');
      document.dispatchEvent(closeNotificationEvent);
      // Modalità + eventuale file pre-scelto dal composer del Wall (chip Foto/Video)
      const detail = (e as CustomEvent)?.detail || {};
      setCreatePostMode(detail.mode === 'post' ? 'post' : 'tweet');
      setCreatePostFile(
        detail.file instanceof File
          ? { file: detail.file, type: detail.fileType === 'video' ? 'video' : 'image' }
          : null
      );
      // Apri il modale wall
      setIsWallModalOpen(true);
    };

    // Ascolta l'evento dal document (come usato in header.tsx)
    document.addEventListener('openCreatePost', handleOpenCreatePost);
    return () => {
      document.removeEventListener('openCreatePost', handleOpenCreatePost);
    };
  }, []);

  // Funzione per gestire la submit del post sul wall
  const handleWallSubmit = async (data: any) => {
    console.log('[WallModalManager] 🚀 Submit post sul wall:', data);
    
    try {
      const response = await fetch('/api/wall', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Errore nella creazione del post');
      }

      const result = await response.json();
      console.log('[WallModalManager] ✅ Post creato:', result);

      // Emetti evento per notificare la pagina wall di aggiornare i post
      window.dispatchEvent(new CustomEvent('wallPostCreated', { detail: { post: result.post } }));
      
      // Invalida la cache per aggiornare la lista post
      queryClient.invalidateQueries({ queryKey: ['/api/wall'] });
    } catch (error) {
      console.error('[WallModalManager] ❌ Errore creazione post:', error);
      throw error;
    }
  };

  return (
    <Suspense fallback={null}>
      <UnifiedWallModal
        isOpen={isWallModalOpen}
        onClose={() => { setIsWallModalOpen(false); setCreatePostFile(null); }}
        onSubmit={handleWallSubmit}
        initialMode={createPostMode}
        initialFile={createPostFile}
      />
    </Suspense>
  );
}

// Wrapper to provide AppContext at the right level
function AppContextWrapper({ children }: { children: React.ReactNode }) {
  const authContext = useContext(AuthContext);

  // Se il context non è ancora disponibile, ritorna un loader
  if (!authContext) {
    return <LoadingScreen />;
  }

  const { user, token } = authContext;

  return (
    <AppProvider
      value={{
        isAuthenticated: !!user,
        setIsAuthenticated: () => {}, // Gestito da AuthProvider
        user,
        setUser: () => {}, // Gestito da AuthProvider
        token,
        setToken: () => {}, // Gestito da AuthProvider
        isLoading: false,
        setIsLoading: () => {},
        isAnyProfileSheetOpen: false,
        setIsAnyProfileSheetOpen: () => {},
        isEventSheetOpen: false,
        setIsEventSheetOpen: () => {},
        eventEditMode: false,
        setEventEditMode: () => {},
        eventEditModeToggle: null,
        setEventEditModeToggle: () => {},
        canEditCurrentEvent: false,
        setCanEditCurrentEvent: () => {},
        profileEditMode: false,
        setProfileEditMode: () => {}
      }}
    >
      {children}
      {/* UnifiedWallModal globale per creare post - z-index sopra l'header */}
      <WallModalManager />
    </AppProvider>
  );
}
